// Asterisk's vocabulary, translated into the customer's.
//
// WHAT THE PBX ACTUALLY SENDS, captured live from Kevin's own inbound call to Lyons Fire's
// station number on 2026-08-13 (recorded off the Live Calls table, four frames):
//
//     00:21:22  dialing  PBX-02  outbound  23038236611  23038236611  <unknown>
//     00:21:22  ringing  PBX-02  outbound  53038236611  53038236611  +13038232313
//     00:21:44  (gone)
//
// One row, replaced in place. Every field except the state changed. Read it against what
// really happened — an outside caller on +1 (303) 823-2313 rang five handsets — and three
// separate things are wrong for an operator:
//
//   1. `direction: outbound` on an INBOUND call. The agent reports the direction of the LEG
//      it is describing (the PBX dialling a handset), not of the call. For a genuine outbound
//      call the same field is right, so the value is a coin flip and this file does not
//      render it. Asked for properly in AGENT_ASKS.
//   2. `from` is our own extension and `to` is the outside party — swapped, for the same
//      reason: the leg's caller is the PBX side.
//   3. The extension is whichever leg arrived last. A ring group rings five and the table
//      showed one, changing every second or two ("it only showed one extension").
//
// And the CDR side, read from `/dashboard/recent-calls` the same evening: one call to a ring
// group writes ONE ROW PER MEMBER LEG — same `calldate`, same `src`, same `dst`, different
// `uniqueid`, one `ANSWERED` among several `NO ANSWER`. Kevin: "one call turns into 4 recent
// calls."
//
// So the rule this file follows: NEVER show a field we cannot stand behind, and translate
// what we can. The two parties are identifiable without trusting direction — one side is an
// extension Verso knows and the other is not — and an extension resolves to the number a
// customer dials, which is the thing an operator recognises.

/** Last ten digits — the fleet names every extension after the DID. */
export function lastTen(value) {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : null;
}

/**
 * Last seven digits — what a RING GROUP is named after.
 *
 * Ring groups are seven digits, not ten: Lyons Fire's is `8236611` for `+1 303 823-6611`.
 * FreePBX types `groupNumber` as int32 and `3038236611` overflows it, so the fleet numbers
 * them off the subscriber number instead. `lastTen` returns null below ten digits, so every
 * ring-group call fell through to the raw value and the operator saw `8236611`.
 */
export function lastSeven(value) {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length >= 7 ? digits.slice(-7) : null;
}

/** Two map entries describing the same line — id when we have one, else the number. */
function sameLine(a, b) {
  if (a.lineId != null && b.lineId != null) return a.lineId === b.lineId;
  return a.number != null && a.number === b.number;
}

/**
 * +13038236611 → (303) 823-6611. Anything that is not a NANP number is returned unchanged:
 * an extension, a SIP URI and "<unknown>" all have to survive this.
 */
export function fmtPhone(value) {
  if (value == null || value === '') return null;
  const raw = String(value).trim();
  const digits = raw.replace(/\D/g, '');
  const ten = (digits.length === 11 && digits.startsWith('1')) ? digits.slice(1)
    : digits.length === 10 ? digits : null;
  // Only for values that LOOK like a dialled number. `13038236611` is an extension in this
  // fleet and formatting it as a phone number would be a second kind of lie.
  if (!ten || !/^\+?1?\d{10}$/.test(raw.replace(/[()\s.-]/g, ''))) return raw;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}

/**
 * An extension, a ring-group number or an E.164 number → the line it belongs to.
 *
 * MATCHED ON TRAILING DIGITS, because the fleet derives every shape from the DID:
 * extensions `13038236611`…`53038236611` and the number `+13038236611` on ten, ring groups
 * on SEVEN — `8236611`, because FreePBX types `groupNumber` as int32 and the ten-digit form
 * overflows it. A value that follows no convention simply does not match, and the caller
 * shows the raw value.
 *
 * This comment used to claim ring groups were `3038236611`, and `lineFor` was written to the
 * comment rather than to a CDR: `lastTen` returns null under ten digits, so every ring-group
 * call showed the operator `8236611` instead of the number and the customer's name. Live
 * rows on 2026-08-14 settled it — Kevin: "the line shows the ring group number and not the
 * more operator friendly line number and subscriber name".
 *
 * @param map  `{ [username]: { number, label, subscriberName, ... } }` from /extension-lines
 */
/**
 * Asterisk's special dialplan extensions, which are NOT destinations.
 *
 * A CDR's `dst` is whatever extension the call last sat in, and for these it is a
 * dialplan label rather than anything dialled:
 *
 *   s        where a route begins
 *   i        the caller pressed an option that does not exist
 *   t        the caller did not press anything in time
 *   h        the hangup handler
 *   failed   the call could not be placed
 *
 * Found live on 2026-08-14: `dst: "i", dcontext: "ivr-2"` rendered in the operator's
 * LINE column as a bare **i**. It is the same shape as the ring-group bug — a value
 * `lineFor` cannot resolve, printed raw — except that here there is no line to find,
 * because the customer's end is implied by the CONTEXT and never appears in `dst`.
 *
 * @returns a plain-language description, or null when `dst` is a real destination
 */
export function describeDialplanStop(dst, dcontext) {
  const what = {
    i: 'caller pressed an option that does not exist',
    t: 'caller did not choose an option in time',
    s: 'call reached the start of a route',
    h: 'call was hung up',
    failed: 'call could not be connected',
  }[String(dst ?? '').trim().toLowerCase()];
  if (!what) return null;

  // `ivr-2` → "IVR 2". Anything else is left alone rather than prettied into a
  // guess — a context we do not recognise is not one we should rename.
  const ctx = String(dcontext ?? '').trim();
  const ivr = ctx.match(/^ivr-(\d+)$/i);
  const where = ivr ? `IVR ${ivr[1]}` : null;
  return where ? `${where} — ${what}` : what;
}

/** True when `dst` is a dialplan label, so no line can be resolved from it. */
export function isDialplanStop(dst) {
  return describeDialplanStop(dst, null) !== null;
}

export function lineFor(value, map) {
  if (!value || !map) return null;
  const direct = map[String(value)];
  if (direct) return direct;

  const ten = lastTen(value);
  if (ten) {
    for (const entry of Object.values(map)) {
      if (lastTen(entry.number) === ten) return entry;
    }
    // A ten-digit value that matches nothing is a number we do not hold. It deliberately
    // does NOT fall through to the seven-digit rule below: (720) 823-6611 and
    // (303) 823-6611 share their last seven and are different customers.
    return null;
  }

  // Seven digits: a ring group. Accept it only when every entry that matches points at the
  // SAME line — across the current fleet nothing collides, but the day two area codes share
  // a subscriber number, naming the wrong customer on a call record is worse than showing
  // the operator a raw number they can look up.
  const seven = lastSeven(value);
  if (!seven) return null;
  let found = null;
  for (const entry of Object.values(map)) {
    if (lastSeven(entry.number) !== seven) continue;
    if (found && !sameLine(found, entry)) return null;
    found ??= entry;
  }
  return found;
}

/** Is this one of ours — an extension Verso holds, or a number one of its lines owns? */
function isLocal(value, map) {
  return Boolean(lineFor(value, map));
}

/**
 * Who is on this call, without guessing which way it went.
 *
 * Returns `{ line, extension, other }` where `line` is the customer's number (the thing to
 * show) and `other` is the far party. Direction is deliberately absent — see the header.
 */
export function callParties({ ext, from, to }, map) {
  const candidates = [ext, from, to].filter((v) => v != null && v !== '' && v !== '<unknown>');
  // Our side: the first value that resolves to a line. `ext` first — it is the field the
  // agent means as "the local extension involved".
  const local = candidates.find((v) => isLocal(v, map)) ?? null;
  // The far party: the first value that is NOT ours. On a leg where both from and to are
  // the same extension (seen live: `from` and `ext` identical), that leaves `to`.
  const other = candidates.find((v) => !isLocal(v, map)) ?? null;
  return {
    line: local ? lineFor(local, map) : null,
    extension: local ? String(local) : null,
    other: other ? String(other) : null,
  };
}

const STATE_WORDS = {
  ringing: 'Ringing',
  dialing: 'Ringing',      // one word for the operator; the difference is ours, not theirs
  ring: 'Ringing',
  up: 'Talking',
  answered: 'Talking',
  bridged: 'Talking',
  hold: 'On hold',
};

/** `bridged` is not a word anybody says on the phone to a customer. */
export function stateWord(state) {
  const key = String(state || '').toLowerCase();
  return STATE_WORDS[key] || (key ? key[0].toUpperCase() + key.slice(1) : '—');
}

/** 0:07, 1:42, 1:02:11 — how long the call has been up. */
export function elapsed(fromMs, now = Date.now()) {
  if (!fromMs) return null;
  const s = Math.max(0, Math.floor((now - fromMs) / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h
    ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    : `${m}:${String(sec).padStart(2, '0')}`;
}

/**
 * One call per row, not one leg per row.
 *
 * A ring group writes a CDR row per member it dialled — same second, same caller, same
 * destination, different `uniqueid` — so a single unanswered call to Lyons Fire appeared four
 * times. There is no `linkedid` in the payload to group by (asked for; see AGENT_ASKS), so
 * the key is what those rows genuinely share: host, caller, destination and call start.
 *
 * The surviving row is the one that says the most: ANSWERED beats NO ANSWER, and the longest
 * billed leg wins among equals — a merged row that reported "no answer" for a call somebody
 * picked up would be worse than the duplicates.
 */
const ANSWERED = new Set(['ANSWERED']);

export function collapseCallLegs(rows) {
  const byCall = new Map();
  for (const r of rows || []) {
    const key = [
      r.agentId || '',
      r.src || '',
      r.dst || '',
      // `calldate` is per call, `ts` can differ by a second between legs.
      r.calldate || r.ts || '',
    ].join('|');
    const prev = byCall.get(key);
    if (!prev) {
      byCall.set(key, { ...r, legs: 1 });
      continue;
    }
    prev.legs += 1;
    const better = ANSWERED.has(String(r.disposition).toUpperCase())
      && !ANSWERED.has(String(prev.disposition).toUpperCase());
    if (better) {
      byCall.set(key, { ...r, legs: prev.legs });
    } else if (!better && (r.billsec || 0) > (prev.billsec || 0)
      && ANSWERED.has(String(r.disposition).toUpperCase()) === ANSWERED.has(String(prev.disposition).toUpperCase())) {
      byCall.set(key, { ...r, legs: prev.legs });
    }
  }
  return [...byCall.values()];
}

/**
 * Fold one `call.state_change` event into the live-calls map.
 *
 * LIVED IN AppShell UNTIL 2026-08-13, inline in a `setState` callback, where the only way to
 * exercise it was to ring a real phone — which is how it went months reporting one extension
 * for a five-phone ring group and "outbound" for a call ringing in. It is the same rule the
 * capture in this file's header settled, so it belongs beside it and under test.
 *
 * What updates: `state`, `ts`, and the set of extensions the call has touched.
 * What sticks: the parties and the direction — first non-empty answer wins, because each
 * later event describes a further LEG (the PBX dialling another handset) and would otherwise
 * overwrite the call's own identity with that leg's.
 *
 * @returns the next map — the same object when nothing changed, so React can skip the render
 */
export function mergeCallEvent(prev, ev, now = Date.now()) {
  const key = ev?.linkedid || ev?.uniqueid || ev?.channel || ev?.id;
  if (!key) return prev;

  const state = String(ev.state || '').toLowerCase();
  if (state === 'ended' || state === 'hangup') {
    if (!(key in prev)) return prev;
    const { [key]: _gone, ...rest } = prev;
    void _gone;
    return rest;
  }

  const seen = prev[key];
  // `<unknown>` is a placeholder, not an answer, and must never be the value that sticks.
  const firstOf = (next, held) => (held ?? (next && next !== '<unknown>' ? next : null));
  const ext = ev.ext ?? ev.extension ?? null;
  const exts = new Set(seen?.exts || []);
  if (ext) exts.add(String(ext));

  return {
    ...prev,
    [key]: {
      key,
      state,
      exts: [...exts],
      from: firstOf(ev.from ?? ev.src, seen?.from),
      to: firstOf(ev.to ?? ev.dst, seen?.to),
      ext: firstOf(ext, seen?.ext),
      direction: firstOf(ev.direction, seen?.direction),
      // Tagged by OrgRelay. With several hosts on one stream, "1001 is ringing" is
      // ambiguous without knowing whose 1001.
      agentId: ev.agent_id ?? seen?.agentId ?? null,
      agentName: ev.agent_name ?? seen?.agentName ?? null,
      startedAt: seen?.startedAt ?? now,
      ts: now,
    },
  };
}
